import { useIntl, FormattedMessage, defineMessage } from 'react-intl';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { faRedo } from '@fortawesome/free-solid-svg-icons/faRedo';
import { faSpinner } from '@fortawesome/free-solid-svg-icons/faSpinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import DOMPurify from 'dompurify';
import PropTypes from 'prop-types';
import React, { useRef, useState, useCallback, useEffect } from 'react';
import { PropTypes as PropTypes$1 } from '@micromag/core';
import { ScreenElement, Transitions } from '@micromag/core/components';
import { useScreenSize, useViewerContext, useScreenRenderContext, useViewerWebView, usePlaybackContext, usePlaybackMediaRef } from '@micromag/core/contexts';
import { useTrackScreenEvent, useDimensionObserver } from '@micromag/core/hooks';
import { isHeaderFilled, isFooterFilled, getFooterProps, isTextFilled, isLabelFilled, getStyleFromColor } from '@micromag/core/utils';
import { useContributionCreate, useContributions } from '@micromag/data';
import Background from '@micromag/element-background';
import Button from '@micromag/element-button';
import Container from '@micromag/element-container';
import Footer from '@micromag/element-footer';
import Header from '@micromag/element-header';
import Heading from '@micromag/element-heading';
import Layout from '@micromag/element-layout';
import Scroll from '@micromag/element-scroll';
import Text from '@micromag/element-text';
import TextInput from '@micromag/element-text-input';

var styles = {"container":"micromag-screen-contribution-container","inner":"micromag-screen-contribution-inner","background":"micromag-screen-contribution-background","buttonSkip":"micromag-screen-contribution-buttonSkip","buttonSubmit":"micromag-screen-contribution-buttonSubmit","content":"micromag-screen-contribution-content","layout":"micromag-screen-contribution-layout","header":"micromag-screen-contribution-header","emptyTitle":"micromag-screen-contribution-emptyTitle","emptyInputName":"micromag-screen-contribution-emptyInputName","emptyInputMessage":"micromag-screen-contribution-emptyInputMessage","emptySubmit":"micromag-screen-contribution-emptySubmit","emptySkip":"micromag-screen-contribution-emptySkip","title":"micromag-screen-contribution-title","interactiveContainer":"micromag-screen-contribution-interactiveContainer","form":"micromag-screen-contribution-form","inputName":"micromag-screen-contribution-inputName","inputMessage":"micromag-screen-contribution-inputMessage","showBack":"micromag-screen-contribution-showBack","formContent":"micromag-screen-contribution-formContent","formLoading":"micromag-screen-contribution-formLoading","loadingIcon":"micromag-screen-contribution-loadingIcon","contributionsContainer":"micromag-screen-contribution-contributionsContainer","contributionsContent":"micromag-screen-contribution-contributionsContent","contributions":"micromag-screen-contribution-contributions","contribution":"micromag-screen-contribution-contribution","footer":"micromag-screen-contribution-footer","disabled":"micromag-screen-contribution-disabled","submitting":"micromag-screen-contribution-submitting","submitted":"micromag-screen-contribution-submitted","showContributions":"micromag-screen-contribution-showContributions","isPlaceholder":"micromag-screen-contribution-isPlaceholder","reset":"micromag-screen-contribution-reset"};

var propTypes = {
  id: PropTypes.string,
  layout: PropTypes.oneOf(['top', 'middle', 'bottom']),
  title: PropTypes$1.headingElement,
  name: PropTypes$1.inputElement,
  message: PropTypes$1.inputElement,
  submit: PropTypes$1.textElement,
  nameStyle: PropTypes$1.textStyle,
  messageStyle: PropTypes$1.textStyle,
  settings: PropTypes.shape({
    canViewAnswers: PropTypes.bool,
    answerButton: PropTypes$1.buttonElement,
    contributionButton: PropTypes$1.buttonElement
  }),
  spacing: PropTypes.number,
  background: PropTypes$1.backgroundElement,
  header: PropTypes$1.header,
  footer: PropTypes$1.footer,
  current: PropTypes.bool,
  preload: PropTypes.bool,
  transitions: PropTypes$1.transitions,
  transitionStagger: PropTypes.number,
  resizeTransitionDuration: PropTypes.number,
  type: PropTypes.string,
  className: PropTypes.string
};
var defaultProps = {
  id: null,
  layout: 'middle',
  title: null,
  name: null,
  message: null,
  submit: null,
  nameStyle: null,
  messageStyle: null,
  settings: null,
  spacing: 20,
  background: null,
  header: null,
  footer: null,
  current: true,
  preload: true,
  transitions: null,
  transitionStagger: 100,
  resizeTransitionDuration: 750,
  type: null,
  className: null
};
var ContributionScreen = function ContributionScreen(_ref) {
  var id = _ref.id,
    layout = _ref.layout,
    title = _ref.title,
    name = _ref.name,
    message = _ref.message,
    submit = _ref.submit,
    nameStyle = _ref.nameStyle,
    messageStyle = _ref.messageStyle,
    settings = _ref.settings,
    spacing = _ref.spacing,
    background = _ref.background,
    header = _ref.header,
    footer = _ref.footer,
    current = _ref.current,
    preload = _ref.preload,
    transitions = _ref.transitions,
    transitionStagger = _ref.transitionStagger,
    resizeTransitionDuration = _ref.resizeTransitionDuration,
    type = _ref.type,
    className = _ref.className;
  var intl = useIntl();
  var screenId = id || 'screen-id';
  var trackScreenEvent = useTrackScreenEvent(type);
  var _useScreenSize = useScreenSize(),
    width = _useScreenSize.width,
    height = _useScreenSize.height,
    resolution = _useScreenSize.resolution;
  var _useViewerContext = useViewerContext(),
    viewerTopHeight = _useViewerContext.topHeight,
    viewerBottomHeight = _useViewerContext.bottomHeight,
    viewerBottomSidesWidth = _useViewerContext.bottomSidesWidth;
  var _useScreenRenderConte = useScreenRenderContext(),
    isView = _useScreenRenderConte.isView,
    isPreview = _useScreenRenderConte.isPreview,
    isPlaceholder = _useScreenRenderConte.isPlaceholder,
    isEdit = _useScreenRenderConte.isEdit,
    isStatic = _useScreenRenderConte.isStatic,
    isCapture = _useScreenRenderConte.isCapture;
  var _useViewerWebView = useViewerWebView(),
    openWebView = _useViewerWebView.open;
  var _usePlaybackContext = usePlaybackContext(),
    muted = _usePlaybackContext.muted;
  var mediaRef = usePlaybackMediaRef(current);
  var backgroundPlaying = current && (isView || isEdit);
  var mediaShouldLoad = current || preload;
  var transitionPlaying = current;
  var transitionDisabled = isStatic || isCapture || isPlaceholder || isPreview || isEdit;
  var scrollingDisabled = !isEdit && transitionDisabled || !current;
  var hasHeader = isHeaderFilled(header);
  var hasFooter = isFooterFilled(footer);
  var footerProps = getFooterProps(footer, {
    isView: isView,
    current: current,
    openWebView: openWebView,
    isPreview: isPreview
  });
  var hasTitle = isTextFilled(title);
  var hasNameLabel = isLabelFilled(name);
  var hasMessageLabel = isLabelFilled(message);
  var hasSubmit = isTextFilled(submit);
  var contributionsRef = useRef(null);
  var formRef = useRef(null);
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    interactiveContainerHeight = _useState2[0],
    setInteractiveContainerHeight = _useState2[1];
  var _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    userName = _useState4[0],
    setUserName = _useState4[1];
  var _useState5 = useState(''),
    _useState6 = _slicedToArray(_useState5, 2),
    userMessage = _useState6[0],
    setUserMessage = _useState6[1];

  // 0 = default, 1 = submitting, 2 = submitted, 3 = resizing, 4 = done
  var _useState7 = useState(isStatic || isCapture ? 4 : 0),
    _useState8 = _slicedToArray(_useState7, 2),
    submitState = _useState8[0],
    setSubmitState = _useState8[1];
  var _useState9 = useState(false),
    _useState0 = _slicedToArray(_useState9, 2),
    hasSubmitted = _useState0[0],
    setHasSubmitted = _useState0[1];
  var _ref2 = settings || {},
    _ref2$canViewAnswers = _ref2.canViewAnswers,
    canViewAnswers = _ref2$canViewAnswers === void 0 ? false : _ref2$canViewAnswers,
    _ref2$skipButton = _ref2.skipButton,
    skipButton = _ref2$skipButton === void 0 ? null : _ref2$skipButton,
    _ref2$backButton = _ref2.backButton,
    backButton = _ref2$backButton === void 0 ? null : _ref2$backButton;
  var answersButton = submitState === 4 ? backButton : skipButton;
  var finalAnswersButton = isTextFilled(answersButton) ? answersButton : _objectSpread(_objectSpread({}, answersButton), {}, {
    body: submitState === 4 ? intl.formatMessage({
      id: "8NyYTH",
      defaultMessage: [{
        "type": 0,
        "value": "Back"
      }]
    }) : intl.formatMessage({
      id: "HpkW4J",
      defaultMessage: [{
        "type": 0,
        "value": "Skip"
      }]
    })
  });
  var onContributionSubmitted = useCallback(function () {
    setSubmitState(2);
    trackScreenEvent('submit_success', "".concat(userName, ": ").concat(userMessage));
    setHasSubmitted(true);
  }, [setSubmitState, setHasSubmitted, trackScreenEvent, userName, userMessage]);
  var _useContributionCreat = useContributionCreate({
      screenId: screenId
    }),
    submitContribution = _useContributionCreat.create;
  var _useContributions = useContributions({
      screenId: screenId,
      opts: {
        autoload: !isPlaceholder
      }
    }),
    contributions = _useContributions.contributions;
  var onNameChange = useCallback(function (e) {
    var value = DOMPurify.sanitize(e.currentTarget.value || '');
    setUserName(value);
  }, [setUserName]);
  var onMessageChange = useCallback(function (e) {
    var value = DOMPurify.sanitize(e.currentTarget.value || '');
    setUserMessage(value);
  }, [setUserMessage]);
  var onContributionReset = useCallback(function () {
    setUserName('');
    setUserMessage('');
    setSubmitState(0);
    setHasSubmitted(false);
  }, [setUserName, setUserMessage, setSubmitState, setHasSubmitted]);
  var nameFilled = useRef(false);
  var onNameBlur = useCallback(function (e) {
    if (!nameFilled.current && e.currentTarget.value.length > 0) {
      nameFilled.current = true;
      trackScreenEvent('input_filled', 'Name', {
        userName: e.currentTarget.value,
        userMessage: userMessage
      });
    }
  }, [trackScreenEvent, userMessage]);
  var messageFilled = useRef(false);
  var onMessageBlur = useCallback(function (e) {
    if (!messageFilled.current && e.currentTarget.value.length > 0) {
      messageFilled.current = true;
      trackScreenEvent('input_filled', 'Message', {
        userName: userName,
        userMessage: e.currentTarget.value
      });
    }
  }, [trackScreenEvent, userName]);

  // Call to Action
  var _useState1 = useState(false),
    _useState10 = _slicedToArray(_useState1, 2),
    scrolledBottom = _useState10[0],
    setScrolledBottom = _useState10[1];
  var swipeUpLinkActive = scrolledBottom && submitState === 4;
  var _useDimensionObserver = useDimensionObserver(),
    footerRef = _useDimensionObserver.ref,
    _useDimensionObserver2 = _useDimensionObserver.height,
    callToActionHeight = _useDimensionObserver2 === void 0 ? 0 : _useDimensionObserver2;
  var onScrolledBottom = useCallback(function (_ref3) {
    var initial = _ref3.initial;
    if (initial) {
      trackScreenEvent('scroll', 'Contributions list');
    }
    setScrolledBottom(true);
  }, [trackScreenEvent, setScrolledBottom]);
  var onScrolledNotBottom = useCallback(function () {
    setScrolledBottom(false);
  }, [setScrolledBottom]);
  var onSubmit = useCallback(function (e) {
    e.preventDefault();
    if (submitState === 0) {
      setInteractiveContainerHeight(formRef.current.offsetHeight);
      setSubmitState(1);
      submitContribution({
        name: userName,
        message: userMessage
      });
      onContributionSubmitted();
      trackScreenEvent('click_submit', "".concat(userName, ": ").concat(userMessage), {
        userName: userName,
        userMessage: userMessage
      });
    }
  }, [submitState, setSubmitState, userName, userMessage, trackScreenEvent, onContributionSubmitted]);
  var onClickSkip = useCallback(function () {
    if (submitState === 4) {
      setSubmitState(0);
      trackScreenEvent('click_skip', 'Skip button');
    } else {
      setSubmitState(4);
      trackScreenEvent('click_back', 'Back button');
    }
  }, [submitState, setSubmitState, trackScreenEvent]);
  useEffect(function () {
    var timeout = null;
    if (submitState === 2) {
      timeout = setTimeout(setSubmitState, resizeTransitionDuration, 4);
      setInteractiveContainerHeight(contributionsRef.current.offsetHeight);
      setSubmitState(3);
    }
    return function () {
      if (submitState === 3 && timeout !== null) {
        clearTimeout(timeout);
      }
    };
  }, [submitState, setInteractiveContainerHeight, setSubmitState, resizeTransitionDuration]);

  // Title

  var items = [/*#__PURE__*/React.createElement(ScreenElement, {
    key: "title",
    placeholder: "title",
    placeholderProps: {
      height: '0.75em'
    },
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "2ZOPe+",
      defaultMessage: [{
        "type": 0,
        "value": "Title"
      }]
    }),
    emptyClassName: styles.emptyTitle,
    isEmpty: !hasTitle
  }, hasTitle ? /*#__PURE__*/React.createElement(Heading, Object.assign({}, title, {
    className: styles.title
  })) : null)];
  var allContributions = [].concat(_toConsumableArray(userName !== null && userName !== '' && userMessage !== null && userMessage !== '' ? [{
    name: userName,
    message: userMessage
  }] : []), _toConsumableArray(contributions || []));

  // Form

  items.push(/*#__PURE__*/React.createElement("div", {
    key: "form",
    className: styles.interactiveContainer,
    style: {
      height: submitState < 4 ? interactiveContainerHeight : null,
      transitionDuration: "".concat(resizeTransitionDuration, "ms")
    }
  }, /*#__PURE__*/React.createElement("form", {
    className: styles.form,
    onSubmit: onSubmit,
    ref: formRef
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.formContent
  }, /*#__PURE__*/React.createElement(ScreenElement, {
    placeholder: "inputText",
    placeholderProps: {
      height: '0.25em'
    },
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "G/ITGY",
      defaultMessage: [{
        "type": 0,
        "value": "Your name"
      }]
    }),
    emptyClassName: styles.emptyInputName,
    isEmpty: !hasNameLabel
  }, /*#__PURE__*/React.createElement(Transitions, {
    transitions: transitions,
    playing: transitionPlaying,
    delay: transitionStagger,
    disabled: transitionDisabled
  }, /*#__PURE__*/React.createElement(TextInput, Object.assign({
    className: styles.inputName
  }, name, {
    value: userName,
    onChange: function onChange(e) {
      return onNameChange(e);
    },
    onBlur: function onBlur(e) {
      return onNameBlur(e);
    },
    focusable: current && isView,
    disabled: isPreview,
    required: true
  })))), /*#__PURE__*/React.createElement(ScreenElement, {
    placeholder: "inputText",
    placeholderProps: {
      height: '0.75em'
    },
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "JAguka",
      defaultMessage: [{
        "type": 0,
        "value": "Your message"
      }]
    }),
    emptyClassName: styles.emptyInputMessage,
    isEmpty: !hasMessageLabel
  }, /*#__PURE__*/React.createElement(Transitions, {
    transitions: transitions,
    playing: transitionPlaying,
    delay: transitionStagger * 2,
    disabled: transitionDisabled
  }, /*#__PURE__*/React.createElement(TextInput, Object.assign({
    className: styles.inputMessage
  }, message, {
    value: userMessage,
    onChange: function onChange(e) {
      return onMessageChange(e);
    },
    onBlur: function onBlur(e) {
      return onMessageBlur(e);
    },
    disabled: isPreview,
    focusable: current && isView,
    multiline: true,
    required: true
  })))), /*#__PURE__*/React.createElement(ScreenElement, {
    placeholder: "button",
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "5uH4x7",
      defaultMessage: [{
        "type": 0,
        "value": "Submit"
      }]
    }),
    emptyClassName: styles.emptySubmit,
    isEmpty: !hasSubmit
  }, /*#__PURE__*/React.createElement(Transitions, {
    transitions: transitions,
    playing: transitionPlaying,
    delay: transitionStagger * 3,
    disabled: transitionDisabled
  }, /*#__PURE__*/React.createElement(Button, {
    type: "submit",
    className: styles.buttonSubmit,
    disabled: isPreview,
    buttonStyle: submit !== null ? submit.buttonStyle : null,
    focusable: current && isView
  }, /*#__PURE__*/React.createElement(Text, Object.assign({}, submit, {
    inline: true
  })))))), /*#__PURE__*/React.createElement("div", {
    className: styles.formLoading
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    className: styles.loadingIcon,
    icon: faSpinner
  }))), canViewAnswers && !hasSubmitted ? /*#__PURE__*/React.createElement(ScreenElement, {
    placeholder: "button",
    emptyLabel: submitState !== 4 ? /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "U2RZp5",
      defaultMessage: [{
        "type": 0,
        "value": "Skip"
      }]
    }) : /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "AzN1aW",
      defaultMessage: [{
        "type": 0,
        "value": "Back"
      }]
    }),
    emptyClassName: styles.emptySkip
  }, /*#__PURE__*/React.createElement(Transitions, {
    transitions: transitions,
    playing: transitionPlaying,
    delay: transitionStagger * 3,
    disabled: transitionDisabled
  }, /*#__PURE__*/React.createElement(Button, {
    type: "button",
    className: classNames([styles.buttonSkip, _defineProperty({}, styles.showBack, submitState === 4)]),
    disabled: isPreview,
    onClick: onClickSkip,
    buttonStyle: finalAnswersButton !== null ? finalAnswersButton.buttonStyle : null,
    focusable: current && isView
  }, /*#__PURE__*/React.createElement(Text, Object.assign({}, finalAnswersButton, {
    inline: true
  }))))) : null, /*#__PURE__*/React.createElement("div", {
    className: styles.contributionsContainer,
    "aria-hidden": submitState === 4 ? null : 'true'
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.contributionsContent
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.contributions,
    ref: contributionsRef
  }, allContributions.map(function (contribution, contributionIndex) {
    var nameInnerStyle = nameStyle !== null ? nameStyle.style || null : null;
    var messageInnerStyle = messageStyle !== null ? messageStyle.style || null : null;
    return /*#__PURE__*/React.createElement("div", {
      key: "contribution-".concat(contributionIndex),
      className: styles.contribution,
      style: nameInnerStyle !== null ? getStyleFromColor(nameInnerStyle.color, 'borderColor') : null
    }, /*#__PURE__*/React.createElement(Heading, {
      className: styles.contributionName,
      body: contribution.name,
      size: 2,
      textStyle: nameInnerStyle
    }), /*#__PURE__*/React.createElement(Text, {
      className: styles.contributionMessage,
      body: contribution.message,
      textStyle: messageInnerStyle
    }));
  })))), hasFooter ? /*#__PURE__*/React.createElement("div", {
    style: {
      height: callToActionHeight
    }
  }) : null));
  var headerElement = !isPlaceholder && hasHeader ? /*#__PURE__*/React.createElement("div", {
    className: styles.header,
    style: {
      paddingBottom: spacing
    }
  }, /*#__PURE__*/React.createElement(Header, header)) : null;
  var showReset = isEdit && submitState === 4;
  return /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.container, _defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty({}, className, className !== null), styles.isPlaceholder, isPlaceholder), styles.submitting, submitState === 1), styles.submitted, submitState > 1), styles.showContributions, submitState === 4)]),
    "data-screen-ready": true
  }, /*#__PURE__*/React.createElement(Container, {
    width: width,
    height: height,
    className: styles.content
  }, showReset ? /*#__PURE__*/React.createElement(Button, {
    className: styles.reset,
    icon: /*#__PURE__*/React.createElement(FontAwesomeIcon, {
      icon: faRedo,
      size: "md"
    }),
    onClick: onContributionReset
  }) : null, /*#__PURE__*/React.createElement("div", {
    className: styles.inner
  }, /*#__PURE__*/React.createElement(Scroll, {
    verticalAlign: layout,
    disabled: scrollingDisabled,
    onScrolledBottom: onScrolledBottom,
    onScrolledNotBottom: onScrolledNotBottom,
    scrolleeClassName: styles.scrollee,
    withShadow: true
  }, /*#__PURE__*/React.createElement(Layout, {
    className: styles.layout,
    style: !isPlaceholder ? {
      padding: spacing,
      paddingTop: hasHeader ? spacing / 2 + (!isPreview ? viewerTopHeight : 0) : spacing / 2,
      paddingBottom: (current && !isPreview ? viewerBottomHeight : 0) + spacing / 2
    } : null
  }, headerElement, items)), !isPlaceholder && hasFooter ? /*#__PURE__*/React.createElement("div", {
    ref: footerRef,
    className: classNames([styles.footer, _defineProperty({}, styles.disabled, !swipeUpLinkActive)]),
    style: {
      paddingLeft: Math.max(viewerBottomSidesWidth, spacing / 2),
      paddingRight: Math.max(viewerBottomSidesWidth, spacing / 2),
      paddingTop: 0,
      paddingBottom: (!isPreview ? viewerBottomHeight : 0) + spacing / 2
    }
  }, /*#__PURE__*/React.createElement(Footer, footerProps)) : null)), !isPlaceholder ? /*#__PURE__*/React.createElement(Background, {
    background: background,
    width: width,
    height: height,
    resolution: resolution,
    playing: backgroundPlaying,
    muted: muted,
    shouldLoad: mediaShouldLoad,
    mediaRef: mediaRef,
    withoutVideo: isPreview,
    className: styles.background
  }) : null);
};
ContributionScreen.propTypes = propTypes;
ContributionScreen.defaultProps = defaultProps;

var definition = {
  id: 'contribution',
  type: 'screen',
  group: {
    label: defineMessage({
      id: "ZiWlL6",
      defaultMessage: [{
        "type": 0,
        "value": "Questions"
      }]
    }),
    order: 7
  },
  title: defineMessage({
    id: "g6b9Lg",
    defaultMessage: [{
      "type": 0,
      "value": "Contribution"
    }]
  }),
  component: ContributionScreen,
  layouts: ['top', 'middle', 'bottom'],
  fields: [{
    name: 'layout',
    type: 'screen-layout',
    defaultValue: 'top',
    label: defineMessage({
      id: "4iBXj2",
      defaultMessage: [{
        "type": 0,
        "value": "Layout"
      }]
    })
  }, {
    name: 'title',
    type: 'heading-element',
    theme: {
      textStyle: 'heading2'
    },
    label: defineMessage({
      id: "N25iDO",
      defaultMessage: [{
        "type": 0,
        "value": "Title"
      }]
    })
  }, {
    name: 'name',
    type: 'input-element',
    theme: {
      textStyle: 'text'
    },
    label: defineMessage({
      id: "nXVNeg",
      defaultMessage: [{
        "type": 0,
        "value": "Name"
      }]
    })
  }, {
    name: 'message',
    type: 'input-element',
    theme: {
      textStyle: 'text'
    },
    label: defineMessage({
      id: "RcLsua",
      defaultMessage: [{
        "type": 0,
        "value": "Message"
      }]
    })
  }, {
    name: 'submit',
    type: 'button-element',
    theme: {
      textStyle: 'button'
    },
    label: defineMessage({
      id: "uAKPQ9",
      defaultMessage: [{
        "type": 0,
        "value": "Submit"
      }]
    })
  }, {
    name: 'nameStyle',
    type: 'item-text-style',
    theme: {
      style: {
        textStyle: 'heading2'
      }
    },
    label: defineMessage({
      id: "SyMSDa",
      defaultMessage: [{
        "type": 0,
        "value": "Published name style"
      }]
    })
  }, {
    name: 'messageStyle',
    type: 'item-text-style',
    theme: {
      style: {
        textStyle: 'text'
      }
    },
    label: defineMessage({
      id: "+SlKOi",
      defaultMessage: [{
        "type": 0,
        "value": "Published message style"
      }]
    })
  }, {
    name: 'settings',
    component: 'toggle-section',
    toggleField: 'canViewAnswers',
    isList: true,
    label: defineMessage({
      id: "9omJnT",
      defaultMessage: [{
        "type": 0,
        "value": "Answer settings"
      }]
    }),
    fields: [{
      name: 'canViewAnswers',
      type: 'toggle',
      isHorizontal: true,
      label: defineMessage({
        id: "xcHW4o",
        defaultMessage: [{
          "type": 0,
          "value": "Can view answers"
        }]
      })
    }, {
      name: 'skipButton',
      type: 'button-element',
      theme: {
        textStyle: 'button'
      },
      label: defineMessage({
        id: "CpCykm",
        defaultMessage: [{
          "type": 0,
          "value": "Skip button"
        }]
      })
    }, {
      name: 'backButton',
      type: 'button-element',
      theme: {
        textStyle: 'button'
      },
      label: defineMessage({
        id: "KIXg9M",
        defaultMessage: [{
          "type": 0,
          "value": "Back button"
        }]
      })
    }]
  }, {
    name: 'background',
    type: 'background',
    label: defineMessage({
      id: "+MPZRu",
      defaultMessage: [{
        "type": 0,
        "value": "Background"
      }]
    })
  }, {
    name: 'header',
    type: 'header',
    label: defineMessage({
      id: "rhuDxI",
      defaultMessage: [{
        "type": 0,
        "value": "Header"
      }]
    }),
    theme: {
      badge: {
        label: {
          textStyle: 'badge'
        },
        boxStyle: 'badge'
      }
    }
  }, {
    name: 'footer',
    type: 'footer',
    label: defineMessage({
      id: "g4nybp",
      defaultMessage: [{
        "type": 0,
        "value": "Footer"
      }]
    }),
    theme: {
      callToAction: {
        label: {
          textStyle: 'cta'
        },
        boxStyle: 'cta'
      }
    }
  }]
};

export { ContributionScreen, definition as default };
